import React, { useState, useEffect } from 'react';
import { 
    Container, Row, Col, Card, CardHeader, CardBody, 
    Table, Button, Input, Label, UncontrolledDropdown, 
    DropdownToggle, DropdownMenu, DropdownItem, Spinner, Modal, ModalHeader, ModalBody, UncontrolledCarousel,
    Nav, NavItem, NavLink, TabContent, TabPane, Form, FormGroup
} from 'reactstrap';
import { Link } from 'react-router-dom';
import moment from 'moment';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import BreadCrumb from '../../Components/Common/BreadCrumb';
import SweetAlert from 'react-bootstrap-sweetalert';

const API_BASE_URL = process.env.REACT_APP_API_URL?.replace(/\/$/, '');

const Prompts = () => {
    document.title = "Prompts Management";
    
    // State for prompt tags
    const [promptTags, setPromptTags] = useState([]);
    const [activeTag, setActiveTag] = useState(null);
    const [loading, setLoading] = useState(false);
    
    // State for prompts
    const [prompts, setPrompts] = useState([]);
    const [loadingPrompts, setLoadingPrompts] = useState(false);
    
    // Modals state
    const [promptModal, setPromptModal] = useState(false);
    const [editPromptModal, setEditPromptModal] = useState(false);
    
    // Form state
    const [newPromptText, setNewPromptText] = useState("");
    const [editPrompt, setEditPrompt] = useState({ _id: '', text: '', tagId: '' });

    // SweetAlert state
    const [deleteAlert, setDeleteAlert] = useState(null);
    const [successAlert, setSuccessAlert] = useState(null);

    // Get auth token
    const getAuthToken = () => {
        return JSON.parse(sessionStorage.getItem("authUser"))?.token || '';
    };

    // Fetch prompt tags
    const fetchPromptTags = async () => {
        try {
            setLoading(true);
            console.log("Fetching from:", `${API_BASE_URL}/promptTags`);
            console.log("Auth Token:", getAuthToken());
            
            const response = await axios.get(`${API_BASE_URL}/promptTags`, {
                headers: {
                    'Authorization': getAuthToken(),
                    'Content-Type': 'application/json'
                }
            });

            console.log("API Response:", response.data);
            const data = response;
            
            if (data.data) {
                console.log("Setting prompt tags:", data.data);
                setPromptTags(data.data);
                // Set the first tag as active if we have tags and no active tag
                if (data.data.length > 0 && !activeTag) {
                    console.log("Setting active tag:", data.data[0]._id);
                    setActiveTag(data.data[0]._id);
                    fetchPrompts(data.data[0]._id);
                }
            }
        } catch (error) {
            console.error('Error fetching prompt tags:', error);
            console.error('Error details:', error.response?.data || error.message);
            toast.error('Failed to fetch prompt tags', {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
        } finally {
            setLoading(false);
        }
    };

    // Fetch prompts for a specific tag
    const fetchPrompts = async (tagId) => {
        try {
            setLoadingPrompts(true);
            console.log("Fetching prompts for tag:", tagId);
            
            const response = await axios.get(`${API_BASE_URL}/prompts`, {
                headers: {
                    'Authorization': getAuthToken(),
                    'Content-Type': 'application/json'
                },
                params: {
                    tagId: tagId // MongoDB will convert string to ObjectId
                }
            });

            console.log("Prompts API Response:", response.data);
            const data = response;
            
            if (data.data) {
                setPrompts(data.data);
            }
        } catch (error) {
            console.error('Error fetching prompts:', error);
            console.error('Error details:', error.response?.data || error.message);
            toast.error('Failed to fetch prompts', {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
        } finally {
            setLoadingPrompts(false);
        }
    };

    // Initial data fetch
    useEffect(() => {
        fetchPromptTags();
    }, []);

    // Add new prompt
    const handleAddPrompt = async () => {
        if (!newPromptText.trim()) {
            toast.error('Prompt text cannot be empty');
            return;
        }

        try {
            setLoading(true);
            const response = await axios.post(`${API_BASE_URL}/prompts`, 
                {
                    text: newPromptText.trim(),
                    tagId: activeTag // MongoDB will handle the conversion to ObjectId
                },
                {
                    headers: {
                        'Authorization': getAuthToken(),
                        'Content-Type': 'application/json'
                    }
                }
            );

            const data = response;
            
            if (data.message === "Created Successfully") {
                toast.success('Prompt added successfully');
                setNewPromptText("");
                setPromptModal(false);
                // Refresh data after operation
                fetchPrompts(activeTag);
            } else {
                toast.error(data.message || 'Failed to add prompt');
            }
        } catch (error) {
            console.error('Error adding prompt:', error);
            console.error('Error details:', error.response?.data || error.message);
            toast.error('Failed to add prompt');
        } finally {
            setLoading(false);
        }
    };

    // Update prompt
    const handleUpdatePrompt = async () => {
        if (!editPrompt.text.trim()) {
            toast.error('Prompt text cannot be empty');
            return;
        }

        try {
            setLoading(true);
            const response = await axios.patch(`${API_BASE_URL}/prompts`,
                {
                    text: editPrompt.text.trim(),
                    tagId: activeTag // MongoDB will handle the conversion to ObjectId
                },
                {
                    headers: {
                        'Authorization': getAuthToken(),
                        'Content-Type': 'application/json'
                    },
                    params: {
                        id: editPrompt._id
                    }
                }
            );

            const data = response;
            
            if (data.message === "Success") {
                toast.success('Prompt updated successfully');
                setEditPromptModal(false);
                // Refresh data after operation
                fetchPrompts(activeTag);
            } else {
                toast.error(data.message || 'Failed to update prompt');
            }
        } catch (error) {
            console.error('Error updating prompt:', error);
            console.error('Error details:', error.response?.data || error.message);
            toast.error('Failed to update prompt');
        } finally {
            setLoading(false);
        }
    };

    // Show delete confirmation
    const confirmDeletePrompt = (promptId, promptText) => {
        const truncatedText = promptText.length > 30 
            ? promptText.substring(0, 30) + '...' 
            : promptText;
            
        setDeleteAlert(
            <SweetAlert
                warning
                showCancel
                confirmBtnText="Yes, delete it!"
                confirmBtnBsStyle="danger"
                cancelBtnBsStyle="default"
                title="Are you sure?"
                onConfirm={() => handleDeletePrompt(promptId)}
                onCancel={() => setDeleteAlert(null)}
            >
                You won't be able to recover this prompt!<br />
                <small className="text-muted mt-2 d-block">"{truncatedText}"</small>
            </SweetAlert>
        );
    };

    // Handle delete prompt after confirmation
    const handleDeletePrompt = async (promptId) => {
        try {
            setLoading(true);
            setDeleteAlert(null);
            
            const response = await axios.delete(`${API_BASE_URL}/prompts`, {
                headers: {
                    'Authorization': getAuthToken(),
                    'Content-Type': 'application/json'
                },
                params: {
                    id: promptId
                }
            });

            const data = response;
            
            if (data.message === "Success") {
                setSuccessAlert(
                    <SweetAlert
                        success
                        title="Deleted!"
                        onConfirm={() => setSuccessAlert(null)}
                    >
                        The prompt has been deleted successfully.
                    </SweetAlert>
                );
                // Refresh data after operation
                fetchPrompts(activeTag);
            } else {
                toast.error(data.message || 'Failed to delete prompt');
            }
        } catch (error) {
            console.error('Error deleting prompt:', error);
            console.error('Error details:', error.response?.data || error.message);
            toast.error('Failed to delete prompt');
        } finally {
            setLoading(false);
        }
    };

    // Render tabs for each prompt tag
    const renderTabs = () => {
        return (
            <Nav tabs className="nav-tabs-custom mb-3">
                {promptTags.map((tag) => (
                    <NavItem key={tag._id}>
                        <NavLink
                            className={activeTag === tag._id ? 'active' : ''}
                            onClick={() => {
                                setActiveTag(tag._id);
                                fetchPrompts(tag._id);
                            }}
                            style={{ cursor: 'pointer' }}
                        >
                            {tag.tag}
                        </NavLink>
                    </NavItem>
                ))}
            </Nav>
        );
    };

    // Render prompts table
    const renderPromptsTable = () => (
        <div className="table-responsive">
            <Table className="table-striped table-nowrap align-middle mb-0">
                <thead className="table-light">
                    <tr>
                        <th>Sl No</th>
                        <th>Prompt Text</th>
                        <th>Created At</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {prompts.map((prompt, index) => (
                        <tr key={prompt._id}>
                            <td>{index + 1}</td>
                            <td>{prompt.text}</td>
                            <td>{moment(prompt.createdAt).format('DD MMM YYYY, hh:mm A')}</td>
                            <td>
                                <div className="d-flex gap-2">
                                    <Button 
                                        color="primary" 
                                        size="sm"
                                        onClick={() => {
                                            setEditPrompt(prompt);
                                            setEditPromptModal(true);
                                        }}
                                    >
                                        <i className="ri-pencil-line"></i>
                                    </Button>
                                    <Button 
                                        color="danger" 
                                        size="sm"
                                        onClick={() => confirmDeletePrompt(prompt._id, prompt.text)}
                                    >
                                        <i className="ri-delete-bin-line"></i>
                                    </Button>
                                </div>
                            </td>
                        </tr>
                    ))}
                    {prompts.length === 0 && (
                        <tr>
                            <td colSpan="4" className="text-center py-4 text-muted">
                                <i className="ri-information-line fs-4 me-1 align-middle"></i>
                                No prompts found for this tag
                            </td>
                        </tr>
                    )}
                </tbody>
            </Table>
        </div>
    );

    // Add Prompt Modal
    const renderAddPromptModal = () => (
        <Modal
            isOpen={promptModal}
            toggle={() => setPromptModal(!promptModal)}
            centered
            size="md"
        >
            <ModalHeader toggle={() => setPromptModal(!promptModal)}>
                Add New Prompt
            </ModalHeader>
            <ModalBody>
                <Form>
                    <FormGroup>
                        <Label for="promptText">Prompt Text</Label>
                        <Input
                            type="textarea"
                            id="promptText"
                            rows="4"
                            placeholder="Enter prompt text"
                            value={newPromptText}
                            onChange={(e) => setNewPromptText(e.target.value)}
                        />
                    </FormGroup>
                    <div className="text-end mt-3">
                        <Button color="light" onClick={() => setPromptModal(false)} className="me-2">
                            Cancel
                        </Button>
                        <Button color="primary" onClick={handleAddPrompt} disabled={loading}>
                            {loading ? <Spinner size="sm" /> : 'Add Prompt'}
                        </Button>
                    </div>
                </Form>
            </ModalBody>
        </Modal>
    );

    // Edit Prompt Modal
    const renderEditPromptModal = () => (
        <Modal
            isOpen={editPromptModal}
            toggle={() => setEditPromptModal(!editPromptModal)}
            centered
            size="md"
        >
            <ModalHeader toggle={() => setEditPromptModal(!editPromptModal)}>
                Edit Prompt
            </ModalHeader>
            <ModalBody>
                <Form>
                    <FormGroup>
                        <Label for="editPromptText">Prompt Text</Label>
                        <Input
                            type="textarea"
                            id="editPromptText"
                            rows="4"
                            placeholder="Enter prompt text"
                            value={editPrompt.text}
                            onChange={(e) => setEditPrompt({...editPrompt, text: e.target.value})}
                        />
                    </FormGroup>
                    <div className="text-end mt-3">
                        <Button color="light" onClick={() => setEditPromptModal(false)} className="me-2">
                            Cancel
                        </Button>
                        <Button color="primary" onClick={handleUpdatePrompt} disabled={loading}>
                            {loading ? <Spinner size="sm" /> : 'Update Prompt'}
                        </Button>
                    </div>
                </Form>
            </ModalBody>
        </Modal>
    );

    return (
        <div className="page-content">
            {/* Sweet Alert for Delete Confirmation */}
            {deleteAlert}
            {successAlert}
            
            <ToastContainer
                position="top-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
            />
            <Container fluid>
                <BreadCrumb title="Prompts Management" pageTitle="Prompts" />

                {loading ? (
                    <div className="text-center mt-5">
                        <Spinner color="primary" />
                        <p className="mt-2">Loading prompt tags...</p>
                    </div>
                ) : (
                    <>
                        {renderTabs()}

                        {promptTags.length > 0 ? (
                            <Row>
                                <Col lg={12}>
                                    <Card>
                                        <CardHeader className="d-flex align-items-center">
                                            <h5 className="card-title mb-0 flex-grow-1">
                                                {promptTags.find(tag => tag._id === activeTag)?.tag} Prompts
                                            </h5>
                                            <div className="d-flex gap-2">
                                                <Button color="primary" onClick={() => setPromptModal(true)}>
                                                    <i className="ri-add-line align-bottom me-1"></i> Add Prompt
                                                </Button>
                                            </div>
                                        </CardHeader>
                                        <CardBody>
                                            {loadingPrompts ? (
                                                <div className="text-center mt-3">
                                                    <Spinner color="primary" />
                                                </div>
                                            ) : (
                                                renderPromptsTable()
                                            )}
                                        </CardBody>
                                    </Card>
                                </Col>
                            </Row>
                        ) : (
                            <Card>
                                <CardBody>
                                    <div className="text-center py-5">
                                        <div className="avatar-md mx-auto mb-4">
                                            <div className="avatar-title bg-light rounded-circle text-primary fs-2">
                                                <i className="ri-chat-quote-line"></i>
                                            </div>
                                        </div>
                                        <h5 className="mb-2">No Prompt Tags Found</h5>
                                        <p className="text-muted mb-4">
                                            Please contact administrator to add prompt tags.
                                        </p>
                                    </div>
                                </CardBody>
                            </Card>
                        )}
                    </>
                )}
            </Container>
            {renderAddPromptModal()}
            {renderEditPromptModal()}
        </div>
    );
};

export default Prompts;